import {
  AnalyticsEvents,
  BackToEmailButtonClickedProperties,
  LoginCompletedMethod,
  LoginCompletedProperties,
  LoginFailedMethod,
  LoginFailedProperties,
  OtpVerificationPageViewedProperties,
  OtpVerifyButtonClickedProperties,
  ResendOtpButtonClickedProperties,
} from '@mondaygpt/analytics-events'
import { useAnalytics } from 'hooks'
import { jwtDecode } from 'jwt-decode'
import { useAppDispatch, useAppSelector } from 'modules/core'
import { useEffect, useRef, useState } from 'react'
import type { VerifyOtpResponseDto } from 'openapi/models/VerifyOtpResponseDto'
import { clearOtpState, requestOtpAction, selectOtpState, verifyOtpAction } from '../slices'
import { OtpVerificationFormView } from './OtpVerificationFormView'

interface Props {
  onBack: () => void
  /** Receives the verify response so the caller can route on requiresBusinessCreation / hasPendingInvite. */
  onSuccess?: (result: VerifyOtpResponseDto) => void
  /** Optional custom className for the submit button (e.g., for social flow styling) */
  buttonClassName?: string
}

export const OtpVerificationForm = ({ onBack, onSuccess, buttonClassName }: Props) => {
  const dispatch = useAppDispatch()
  const { track, identify, setUserProperties } = useAnalytics()
  const { email, isVerifying, error, isRequesting } = useAppSelector(selectOtpState)
  const [code, setCode] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // Track page view when component mounts
  useEffect(() => {
    if (email) {
      track(AnalyticsEvents.Authentication.OTP_VERIFICATION_PAGE_VIEWED, {
        email: email,
      } satisfies OtpVerificationPageViewedProperties)
    }
  }, [track, email])

  useEffect(() => {
    // Start cooldown timer when component mounts
    setResendCooldown(30)

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    // Set up the cooldown timer
    if (resendCooldown > 0) {
      timerRef.current = setInterval(() => {
        setResendCooldown(prev => Math.max(0, prev - 1))
      }, 1000) as NodeJS.Timeout
    } else if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [resendCooldown])

  const handleSubmit = async () => {
    if (!email || code.length !== 6) return

    track(AnalyticsEvents.Authentication.OTP_VERIFY_BUTTON_CLICKED, {
      email: email,
    } satisfies OtpVerifyButtonClickedProperties)

    try {
      const result = await dispatch(verifyOtpAction({ email, code })).unwrap()

      // Extract user ID from JWT token
      let userId = 'unknown'
      try {
        const decoded = jwtDecode<{ sub: string }>(result.tokens.accessToken)
        userId = decoded.sub
      } catch (error) {
        console.error('Failed to decode JWT token for analytics:', error)
      }

      // Identify the user in analytics
      identify(userId)

      // Set basic user properties from authentication using Mixpanel official fields
      setUserProperties({
        $email: email,
        login_method: 'email',
        last_login: new Date().toISOString(),
      })

      // Track successful login completion
      track(AnalyticsEvents.Authentication.LOGIN_COMPLETED, {
        method: LoginCompletedMethod.EMAIL,
        user_id: userId,
      } satisfies LoginCompletedProperties)

      // Call success callback if provided
      onSuccess?.(result)
    } catch (err) {
      // Track login failed event for OTP verification failure
      track(AnalyticsEvents.Authentication.LOGIN_FAILED, {
        method: LoginFailedMethod.EMAIL,
        error_message: err instanceof Error ? err.message : 'OTP verification failed',
        error_code: 'otp_verification_failed',
      } satisfies LoginFailedProperties)

      // Error is handled in the reducer
      console.error('OTP verification failed:', err)
      // No need to do anything here as the error will be in state
    }
  }

  const handleResendCode = async () => {
    if (!email || resendCooldown > 0) return

    track(AnalyticsEvents.Authentication.RESEND_OTP_BUTTON_CLICKED, {
      email: email,
    } satisfies ResendOtpButtonClickedProperties)

    try {
      await dispatch(requestOtpAction(email)).unwrap()
      setResendCooldown(30)
      setCode('')
    } catch (err) {
      console.error('Failed to resend OTP:', err)
    }
  }

  const handleBack = () => {
    track(AnalyticsEvents.Authentication.BACK_TO_EMAIL_BUTTON_CLICKED, {
      email: email || '',
    } satisfies BackToEmailButtonClickedProperties)

    dispatch(clearOtpState())
    onBack()
  }

  return (
    <OtpVerificationFormView
      email={email || ''}
      code={code}
      setCode={setCode}
      isVerifying={isVerifying}
      isRequesting={isRequesting}
      error={error}
      resendCooldown={resendCooldown}
      onSubmit={handleSubmit}
      onResendCode={handleResendCode}
      onBack={handleBack}
      buttonClassName={buttonClassName}
    />
  )
}
